import { readRange, readUint64, toSafeNumber } from './bytes'
import { MAX_ARCHIVE_ENTRIES, MAX_COMPRESSED_HEADER } from './constants'
import { ArchiveError } from './errors'
import type { PhotoRef } from './imageIndex'

const SIG_EOCD = 0x06054b50
const SIG_EOCD64 = 0x06064b50
const SIG_LOCATOR64 = 0x07064b50
const SIG_CENTRAL = 0x02014b50
const SIG_LOCAL = 0x04034b50
const FLAG_ENCRYPTED = 0x0001
const FLAG_UTF8 = 0x0800

const CP437_HIGH =
  'ÇüéâäàåçêëèïîìÄÅÉæÆôöòûùÿÖÜ¢£¥₧ƒáíóúñÑªº¿⌐¬½¼¡«»░▒▓│┤╡╢╖╕╣║╗╝╜╛┐└┴┬├─┼╞╟╚╔╩╦╠═╬╧╨╤╥╙╘╒╓╫╪┘┌█▄▌▐▀αßΓπΣσµτΦΘΩδ∞φε∩≡±≥≤⌠⌡÷≈°∙·√ⁿ²■\u00a0'

const utf8 = new TextDecoder('utf-8')

export const decodeZipName = (bytes: Uint8Array, isUtf8: boolean): string => {
  if (isUtf8 || bytes.every(byte => byte < 0x80)) return utf8.decode(bytes)
  let out = ''
  for (const byte of bytes) out += byte < 0x80 ? String.fromCharCode(byte) : CP437_HIGH[byte - 0x80]
  return out
}

export const findEndOfCentralDirectory = (view: DataView): number => {
  for (let offset = view.byteLength - 22; offset >= 0; offset--) {
    if (view.getUint32(offset, true) !== SIG_EOCD) continue
    if (offset + 22 + view.getUint16(offset + 20, true) === view.byteLength) return offset
  }
  return -1
}

export const parseZipCentralDirectory = (bytes: Uint8Array): PhotoRef[] => {
  const central = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  const entries: PhotoRef[] = []
  let position = 0
  let seen = 0
  while (position + 46 <= central.byteLength) {
    if (central.getUint32(position, true) !== SIG_CENTRAL) break
    if (++seen > MAX_ARCHIVE_ENTRIES) throw new ArchiveError('header-too-large')
    const flags = central.getUint16(position + 8, true)
    const method = central.getUint16(position + 10, true)
    const nameLength = central.getUint16(position + 28, true)
    const extraLength = central.getUint16(position + 30, true)
    const commentLength = central.getUint16(position + 32, true)
    const nameStart = position + 46
    const extraEnd = nameStart + nameLength + extraLength
    if (extraEnd + commentLength > central.byteLength) throw new ArchiveError('unsupported')
    let compressedSize = central.getUint32(position + 20, true)
    let uncompressedSize = central.getUint32(position + 24, true)
    let headerOffset = central.getUint32(position + 42, true)
    const name = decodeZipName(bytes.subarray(nameStart, nameStart + nameLength), (flags & FLAG_UTF8) !== 0)

    let extra = nameStart + nameLength
    while (extra + 4 <= extraEnd) {
      const id = central.getUint16(extra, true)
      const length = central.getUint16(extra + 2, true)
      const fieldEnd = extra + 4 + length
      if (fieldEnd > extraEnd) throw new ArchiveError('unsupported')
      if (id === 0x0001) {
        let field = extra + 4
        const next = () => {
          if (field + 8 > fieldEnd) throw new ArchiveError('unsupported')
          const value = toSafeNumber(readUint64(central, field))
          field += 8
          return value
        }
        if (uncompressedSize === 0xffffffff) uncompressedSize = next()
        if (compressedSize === 0xffffffff) compressedSize = next()
        if (headerOffset === 0xffffffff) headerOffset = next()
      }
      extra = fieldEnd
    }

    if (!name.endsWith('/')) {
      entries.push({
        name,
        kind: 'zip',
        method,
        offset: headerOffset,
        size: compressedSize,
        uncompressedSize,
        ...((flags & FLAG_ENCRYPTED) !== 0 ? { encrypted: true as const } : {}),
      })
    }
    position = extraEnd + commentLength
  }
  return entries
}

export async function readZipIndex(file: Blob): Promise<PhotoRef[]> {
  const tailLength = Math.min(file.size, 66560)
  const tailBytes = await readRange(file, file.size - tailLength, file.size, MAX_COMPRESSED_HEADER)
  const tail = new DataView(tailBytes.buffer, tailBytes.byteOffset, tailBytes.byteLength)
  const eocd = findEndOfCentralDirectory(tail)
  if (eocd < 0) throw new ArchiveError('not-archive')

  let size = tail.getUint32(eocd + 12, true)
  let offset = tail.getUint32(eocd + 16, true)

  if (eocd >= 20 && tail.getUint32(eocd - 20, true) === SIG_LOCATOR64) {
    const at = toSafeNumber(readUint64(tail, eocd - 12))
    const zip64Bytes = await readRange(file, at, at + 56, MAX_COMPRESSED_HEADER)
    const zip64 = new DataView(zip64Bytes.buffer, zip64Bytes.byteOffset, zip64Bytes.byteLength)
    if (zip64.getUint32(0, true) !== SIG_EOCD64) throw new ArchiveError('unsupported')
    const directorySize = readUint64(zip64, 40)
    if (directorySize > BigInt(MAX_COMPRESSED_HEADER)) throw new ArchiveError('header-too-large')
    size = Number(directorySize)
    offset = toSafeNumber(readUint64(zip64, 48))
  } else if (size === 0xffffffff || offset === 0xffffffff) {
    throw new ArchiveError('unsupported')
  }

  if (size > MAX_COMPRESSED_HEADER) throw new ArchiveError('header-too-large')
  const central = await readRange(file, offset, offset + size, MAX_COMPRESSED_HEADER)
  return parseZipCentralDirectory(central)
}

export async function readZipEntryBlob(
  file: Blob,
  entry: PhotoRef,
  tooLarge: 'photo-too-large' | 'geojson-too-large' = 'photo-too-large'
): Promise<Blob> {
  if (entry.encrypted) throw new ArchiveError('encrypted', entry.name)
  const headerBytes = await readRange(file, entry.offset, entry.offset + 30, MAX_COMPRESSED_HEADER)
  const header = new DataView(headerBytes.buffer, headerBytes.byteOffset, headerBytes.byteLength)
  if (header.getUint32(0, true) !== SIG_LOCAL) throw new ArchiveError('photo-failed', entry.name)
  const start = entry.offset + 30 + header.getUint16(26, true) + header.getUint16(28, true)
  if (start + entry.size > file.size) throw new ArchiveError('out-of-range')
  const raw = file.slice(start, start + entry.size)
  if (entry.method === 0) return raw
  if (entry.method !== 8) throw new ArchiveError('unsupported')
  if (typeof DecompressionStream !== 'function') throw new ArchiveError('unsupported')
  return readCappedInflate(raw, entry.uncompressedSize, tooLarge)
}

const readCappedInflate = async (
  raw: Blob,
  maxBytes: number,
  tooLarge: 'photo-too-large' | 'geojson-too-large'
): Promise<Blob> => {
  const reader = raw.stream().pipeThrough(new DecompressionStream('deflate-raw')).getReader()
  const chunks: Uint8Array[] = []
  let total = 0
  let released = false
  try {
    for (;;) {
      let result: ReadableStreamReadResult<Uint8Array>
      try {
        result = await reader.read()
      } catch {
        released = true
        throw new ArchiveError('photo-failed')
      }
      if (result.done) break
      total += result.value.byteLength
      if (total > maxBytes) {
        await reader.cancel()
        released = true
        throw new ArchiveError(tooLarge)
      }
      chunks.push(result.value)
    }
  } finally {
    if (!released) reader.releaseLock()
  }
  return new Blob(chunks as BlobPart[])
}
